import type { CSSProperties, ReactElement, RefObject } from "react";
import { useEffect, useRef } from "react";
import { animate, motion, useMotionValue, useMotionValueEvent, useTransform, type MotionValue, } from "framer-motion";
import type { ScrollCommentData } from "@/components/ui/Cchatteaser/Cchatteaser.data";
import { SECTION_LABEL_AIS, SECTION_LABEL_UNITED, } from "./chatteaser3.constants";
import { CHATTEASER3_ANIM_PHASE, CHATTEASER3_CONTENT_FADE_PRESET, TAB_ANIM_PRESET, } from "./chatteaser3Anim";
import { measureChatTeaser3Layout } from "./chatteaser3Layout";
import { ChatMessage, SectionHeader } from "./chatteaser3Messages";
import { MESSAGES_INNER_STYLE, UNITED_CARD_STYLE, UNITED_HEADER_STYLE, UNITED_SCROLLER_STYLE, } from "./chatteaser3.styles";
type AnimPhaseLocal = (typeof CHATTEASER3_ANIM_PHASE)[keyof typeof CHATTEASER3_ANIM_PHASE];
export const CHATTEASER3_UNITED_TAB_HEIGHT_MODE = {
    CoverOnExit: "coverOnExit",
    CollapseOnExit: "collapseOnExit",
} as const;
type UnitedTabHeightModeLocal = (typeof CHATTEASER3_UNITED_TAB_HEIGHT_MODE)[keyof typeof CHATTEASER3_UNITED_TAB_HEIGHT_MODE];
type ContentFadePresetLocal = (typeof CHATTEASER3_CONTENT_FADE_PRESET)[keyof typeof CHATTEASER3_CONTENT_FADE_PRESET];
const COLLAPSED_CARD_HEIGHT = 0;
const INSTANT_TRANSITION = { duration: 0 };
const MOBILE_COLLAPSE_EXIT_TRANSITION = {
    duration: 0.08,
    ease: [0.4, 0, 1, 1],
} as const;
const CONTENT_SLED_STYLE: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};
interface Chatteaser3UnitedCardProps {
    animPhase: AnimPhaseLocal;
    contentFadePreset?: ContentFadePresetLocal;
    displayedTab: number;
    introActive: boolean;
    introUnitedHeight: number;
    introUnitedOpacity: number;
    introMessagesOpacity: number;
    introMessagesRise: number;
    layout: ReturnType<typeof measureChatTeaser3Layout>;
    layoutVersion: MotionValue<number>;
    messageScroll: MotionValue<number>;
    messagesInnerRef?: RefObject<HTMLDivElement | null>;
    restHeight: MotionValue<number>;
    tabHeightMode?: UnitedTabHeightModeLocal;
    united: readonly ScrollCommentData[];
    unitedHeaderRef?: RefObject<HTMLDivElement | null>;
    unitedScrollerRef?: RefObject<HTMLDivElement | null>;
}
export function Chatteaser3UnitedCard({ animPhase, contentFadePreset = CHATTEASER3_CONTENT_FADE_PRESET.Default, displayedTab, introActive, introUnitedHeight, introUnitedOpacity, introMessagesOpacity, introMessagesRise, layout, layoutVersion, messageScroll, messagesInnerRef, restHeight, tabHeightMode = CHATTEASER3_UNITED_TAB_HEIGHT_MODE.CoverOnExit, united, unitedHeaderRef, unitedScrollerRef, }: Chatteaser3UnitedCardProps): ReactElement {
    const messageTranslateY = useTransform(messageScroll, (scrollTop) => -scrollTop);
    const fullHeight = layout.wrapperHeight;
    const enterRestHeight = Math.max(0, layout.wrapperHeight - layout.soloBottom);
    const isExit = animPhase === CHATTEASER3_ANIM_PHASE.Exit;
    const isEnter = animPhase === CHATTEASER3_ANIM_PHASE.Enter;
    const isScrollDrivingHeight = !introActive && !isExit && !isEnter;
    const collapseOnExit = tabHeightMode === CHATTEASER3_UNITED_TAB_HEIGHT_MODE.CollapseOnExit;
    const exitHeight = collapseOnExit ? COLLAPSED_CARD_HEIGHT : fullHeight;
    const enterInitialHeight = collapseOnExit ? COLLAPSED_CARD_HEIGHT : fullHeight;
    const exitHeightTransition = collapseOnExit
        ? MOBILE_COLLAPSE_EXIT_TRANSITION
        : TAB_ANIM_PRESET.expandTransition;
    const enterHeightTransition = TAB_ANIM_PRESET.retractTransition;
    // On tab change this height path is mode-driven: desktop covers on exit, while
    // mobile collapses the leaving AIS UNITED block and expands the entering one.
    // SINGLE OWNER for the card height. One MotionValue drives `style.height` at
    // all times; we NEVER also animate `height` via Framer's `animate` prop. The
    // old code flipped ownership between `animate.height` (exit/enter/intro) and
    // `style.height = restHeight` (idle) on the same element — and the handoff at
    // the enter→idle boundary made real Chrome mis-paint the card (a compositor
    // glitch invisible to getBoundingClientRect). Idle follows the scroll-driven
    // rest height; transitions animate this same value imperatively.
    const initialCardHeight = introActive
        ? introUnitedHeight
        : isEnter
            ? enterInitialHeight
            : enterRestHeight;
    const cardHeight = useMotionValue(initialCardHeight);
    const isScrollDrivingHeightRef = useRef(isScrollDrivingHeight);
    useEffect(() => {
        isScrollDrivingHeightRef.current = isScrollDrivingHeight;
    }, [isScrollDrivingHeight]);
    useMotionValueEvent(restHeight, "change", (value) => {
        if (isScrollDrivingHeightRef.current)
            cardHeight.set(value);
    });
    useEffect(() => {
        if (introActive) {
            cardHeight.set(introUnitedHeight);
            return undefined;
        }
        if (animPhase === CHATTEASER3_ANIM_PHASE.Exit) {
            const controls = animate(cardHeight, exitHeight, exitHeightTransition);
            return () => controls.stop();
        }
        if (animPhase === CHATTEASER3_ANIM_PHASE.Enter) {
            const controls = animate(cardHeight, enterRestHeight, enterHeightTransition);
            return () => controls.stop();
        }
        // Idle: hand back to the scroll-driven rest height (the listener keeps it synced).
        cardHeight.set(restHeight.get());
        return undefined;
    }, [
        animPhase,
        introActive,
        introUnitedHeight,
        exitHeight,
        exitHeightTransition,
        enterRestHeight,
        enterHeightTransition,
        cardHeight,
        restHeight,
    ]);
    // Content fades out under the cover, then fades + rises back in on reveal.
    const sledAnimate = introActive
        ? { opacity: introMessagesOpacity, y: introMessagesRise }
        : { opacity: isExit ? 0 : 1, y: 0 };
    const sledTransition = introActive
        ? INSTANT_TRANSITION
        : isExit
            ? contentFadePreset.contentOutTransition
            : contentFadePreset.contentInTransition;
    const sledInitial: false | {
        opacity: number;
        y: number;
    } = !introActive && isEnter
        ? { opacity: 0, y: TAB_ANIM_PRESET.contentRiseY }
        : false;
    return (
      <motion.div data-el="Cchatteaser3/UnitedCard/Root" style={{ ...UNITED_CARD_STYLE, height: cardHeight }} initial={false} animate={{ opacity: introActive ? introUnitedOpacity : 1 }} transition={INSTANT_TRANSITION}>
        <motion.div key={displayedTab} style={{ ...CONTENT_SLED_STYLE, height: restHeight }} initial={sledInitial} animate={sledAnimate} transition={sledTransition}>
          <div ref={unitedHeaderRef} style={UNITED_HEADER_STYLE}>
            <SectionHeader first={SECTION_LABEL_AIS} second={SECTION_LABEL_UNITED} mintFirst/>
          </div>
          <div data-el="Cchatteaser3/UnitedCard/Viewport" ref={unitedScrollerRef} style={UNITED_SCROLLER_STYLE}>
            <motion.div style={{ y: messageTranslateY }}>
              <div data-el="Cchatteaser3/UnitedCard/MessageList" ref={messagesInnerRef} style={MESSAGES_INNER_STYLE}>
                {united.map((comment, index) => (<ChatMessage key={`${comment.name}-${comment.date}-${index}`} comment={comment} layoutVersion={layoutVersion} messageScroll={messageScroll} rect={layout.rects[index]}/>))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    );
}
